import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { isAdminSessionExpiredError } from "../../lib/adminAuth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Plus, Trash2, Edit3 } from "lucide-react";

const LETTERS = ["A", "B", "C", "D"];
const EMPTY_QUESTION = {
  id: null,
  text: "",
  answers: ["", "", "", ""],
  correct_index: 0,
  is_active: true,
  reveal_time_seconds: 45,
  theme: "",
};

const QuestionsTab = () => {
  const [questions, setQuestions] = useState([]);
  const [editingQ, setEditingQ] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const activeCount = questions.filter((q) => q.is_active).length;

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/questions");
      setQuestions(data.questions);
    } catch (err) {
      if (isAdminSessionExpiredError(err)) return;
      toast.error("Erreur de chargement des questions");
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openNew = () => {
    setEditingQ({ ...EMPTY_QUESTION, answers: [...EMPTY_QUESTION.answers] });
    setDialogOpen(true);
  };

  const openEdit = (q) => {
    setEditingQ({ ...q, answers: [...q.answers], theme: q.theme || "" });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!editingQ.text.trim()) return toast.error("Texte requis");
    if (editingQ.answers.length !== 4) return toast.error("4 propositions requises");
    if (editingQ.correct_index < 0 || editingQ.correct_index > 3) return toast.error("Bonne réponse invalide");
    const revealTime = Number(editingQ.reveal_time_seconds) || 45;
    if (revealTime < 5 || revealTime > 600) return toast.error("Temps de réflexion entre 5 et 600 secondes");

    try {
      const body = {
        text: editingQ.text,
        answers: editingQ.answers,
        correct_index: Number(editingQ.correct_index),
        is_active: editingQ.is_active,
        reveal_time_seconds: revealTime,
        theme: editingQ.theme?.trim() || null,
      };
      if (editingQ.id) {
        await api.put(`/admin/questions/${editingQ.id}`, body);
        toast.success("Question mise à jour");
      } else {
        await api.post("/admin/questions", body);
        toast.success("Question créée");
      }
      setDialogOpen(false);
      refresh();
    } catch (err) {
      if (isAdminSessionExpiredError(err)) return;
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette question ?")) return;
    try {
      await api.delete(`/admin/questions/${id}`);
      toast.success("Question supprimée");
      refresh();
    } catch (err) {
      if (isAdminSessionExpiredError(err)) return;
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const toggleActive = async (q, nextActive) => {
    setTogglingId(q.id);
    setQuestions((prev) =>
      prev.map((item) => (item.id === q.id ? { ...item, is_active: nextActive } : item)),
    );
    try {
      await api.put(`/admin/questions/${q.id}`, { is_active: nextActive });
      toast.success(nextActive ? "Question activée" : "Question désactivée");
    } catch (err) {
      setQuestions((prev) =>
        prev.map((item) => (item.id === q.id ? { ...item, is_active: q.is_active } : item)),
      );
      if (isAdminSessionExpiredError(err)) return;
      toast.error(err?.response?.data?.detail || "Impossible de modifier le statut");
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-white/60">
          Total : <span className="font-bold text-lg text-white">{questions.length}</span>
          <span className="mx-2 text-white/30">·</span>
          Actives : <span className="font-bold text-lg text-md-cyan">{activeCount}</span>
        </div>
        <Button onClick={openNew} className="md-btn-solid" data-testid="new-question-button">
          <Plus size={16} className="mr-2" /> Nouvelle question
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {questions.map((q) => (
          <div
            key={q.id}
            className={`md-card p-5 transition-opacity ${q.is_active ? "" : "opacity-75"}`}
            data-testid={`question-card-${q.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                {q.theme ? (
                  <span className="inline-block mb-1.5 text-[10px] uppercase tracking-widest font-semibold text-md-cyan/90 bg-md-cyan/10 border border-md-cyan/25 px-2 py-0.5 rounded">
                    {q.theme}
                  </span>
                ) : null}
                <div className="font-semibold text-lg text-white leading-snug">{q.text}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="flex items-center gap-2 pr-1 border-r border-white/10">
                  <Switch
                    checked={Boolean(q.is_active)}
                    onCheckedChange={(checked) => toggleActive(q, checked)}
                    disabled={togglingId === q.id}
                    className="data-[state=checked]:bg-md-cyan"
                    data-testid={`question-active-toggle-${q.id}`}
                    aria-label={q.is_active ? "Désactiver la question" : "Activer la question"}
                  />
                  <span
                    className={`text-[10px] uppercase tracking-widest font-semibold whitespace-nowrap ${
                      q.is_active ? "text-md-cyan" : "text-white/45"
                    }`}
                  >
                    {q.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
                <Button size="sm" variant="ghost" onClick={() => openEdit(q)} className="text-white/70 hover:text-white">
                  <Edit3 size={14} />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(q.id)} className="text-[#EF4444] hover:text-[#EF4444]">
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
            <ul className="mt-3 space-y-1.5">
              {q.answers.map((a, idx) => (
                <li
                  key={`${q.id}-${idx}`}
                  className={`text-sm px-3 py-2 rounded-md border ${idx === q.correct_index ? "bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30" : "bg-white/5 text-white/80 border-white/10"}`}
                >
                  <span className="font-bold mr-2">{LETTERS[idx]}</span>
                  {a || <span className="italic text-white/40">(vide)</span>}
                </li>
              ))}
            </ul>
            <div className="mt-3 text-xs text-white/50 uppercase tracking-widest">
              Réflexion : <span className="text-white/80 font-semibold">{q.reveal_time_seconds ?? 45}s</span>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-md-panel border-md-cyan/15 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-bold text-2xl">
              {editingQ?.id ? "Modifier la question" : "Nouvelle question"}
            </DialogTitle>
          </DialogHeader>
          {editingQ && (
            <div className="space-y-4">
              <div>
                <Label className="text-white/70">Question</Label>
                <Textarea
                  value={editingQ.text}
                  onChange={(e) => setEditingQ({ ...editingQ, text: e.target.value })}
                  className="md-input mt-1"
                  data-testid="question-text-input"
                />
              </div>
              <div>
                <Label className="text-white/70">Thème (optionnel)</Label>
                <Input
                  value={editingQ.theme || ""}
                  onChange={(e) => setEditingQ({ ...editingQ, theme: e.target.value })}
                  placeholder="Ex. Animaux, Géographie, Sport…"
                  maxLength={64}
                  className="md-input mt-1"
                  data-testid="question-theme-input"
                />
                <p className="text-xs text-white/40 mt-1">
                  Si renseigné, ce thème n’apparaîtra qu’une fois par partie. Laisser vide n’exclut pas la question.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-3">
                {editingQ.answers.map((a, idx) => (
                  <div key={`ans-${idx}`} className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setEditingQ({ ...editingQ, correct_index: idx })}
                      className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${editingQ.correct_index === idx ? "bg-emerald-500 text-white" : "bg-white/10 text-white/60"}`}
                      data-testid={`correct-${idx}`}
                      title="Marquer comme bonne réponse"
                    >
                      {LETTERS[idx]}
                    </button>
                    <Input
                      value={a}
                      onChange={(e) => {
                        const answers = [...editingQ.answers];
                        answers[idx] = e.target.value;
                        setEditingQ({ ...editingQ, answers });
                      }}
                      placeholder={`Proposition ${LETTERS[idx]}`}
                      className="md-input flex-1"
                      data-testid={`answer-input-${idx}`}
                    />
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Label className="text-white/70">Temps de réflexion (secondes)</Label>
                  <Input
                    type="number"
                    min={5}
                    max={600}
                    value={editingQ.reveal_time_seconds}
                    onChange={(e) => setEditingQ({ ...editingQ, reveal_time_seconds: Number(e.target.value) || 0 })}
                    className="md-input mt-1"
                    data-testid="reveal-time-input"
                  />
                  <p className="text-xs text-white/40 mt-1">
                    Temps avant l'ouverture des mises (défaut 45s). Pas de chrono visible pendant cette phase.
                  </p>
                </div>
                <div className="flex items-center gap-2 pt-5">
                  <Switch
                    id="qactive"
                    checked={Boolean(editingQ.is_active)}
                    onCheckedChange={(checked) => setEditingQ({ ...editingQ, is_active: checked })}
                    className="data-[state=checked]:bg-md-cyan"
                  />
                  <Label htmlFor="qactive" className="text-white/70">
                    Question active
                  </Label>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-white/70">Annuler</Button>
            <Button onClick={save} className="md-btn-solid" data-testid="save-question-button">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default QuestionsTab;
